import type {
  Nullish,
  SubscriptionSummary,
  TrialSummary,
} from '@witmetrics/utils';
import { isSubscriptionLocked } from '@witmetrics/utils/subscriptions';
import { formatTextDate } from '@witmetrics/utils/time';
import {
  isExpiredTrial,
  isNotStartedTrial,
  isPendingTrial,
  isRejectedTrial,
} from '@/utils/trials';
import type { LockoutContent, LockoutReason } from './types';
import { LOCKOUT_REASON } from './constants';

const {
  TRIAL_EXPIRED,
  TRIAL_NOT_STARTED,
  TRIAL_PENDING,
  TRIAL_REJECTED,
  SUBSCRIPTION_INACTIVE,
  LOCKED,
} = LOCKOUT_REASON;

export const classes = {
  wrapper: 'relative place-center size-full overflow-hidden',
  card: 'relative z-10 flex w-[420px] max-w-[calc(100%-40px)] flex-col items-center rounded-[26px] border border-solid border-white/70 bg-[linear-gradient(160deg,rgba(255,255,255,0.55),rgba(255,255,255,0.32))] px-8 pb-7 pt-9 shadow-[0_32px_80px_-28px_rgba(70,60,90,0.4),inset_0_1px_0_rgba(255,255,255,0.6)] backdrop-blur-[28px] dark:border-white/10 dark:bg-none dark:bg-[#332e43]/[0.84] dark:shadow-[0_32px_80px_-28px_rgba(0,0,0,0.6)]',
  iconTile:
    'place-center size-14 flex-none rounded-2xl bg-grad text-white shadow-[0_12px_26px_-9px_rgba(241,90,43,0.45)] dark:bg-grad-dark',
  title:
    'mt-5 text-center text-xl font-extrabold tracking-[-0.01em] text-taupe-3 dark:text-mauve-1',
  message:
    'mt-2.5 text-center text-[13px] font-normal leading-[1.6] text-taupe-2 dark:text-mauve-2',
  actionButton:
    'mt-7 w-full rounded-[13px] py-[13px] text-sm shadow-[0_12px_26px_-9px_rgba(241,90,43,0.42)]',
  secondaryActions: 'mt-3 flex w-full gap-2',
  secondaryButton: 'flex-1 rounded-[13px] py-2.5 text-[13px]',
};

export function getLockoutReason(
  trial: TrialSummary | Nullish,
  subscription: SubscriptionSummary | Nullish
): LockoutReason {
  if (isExpiredTrial(trial)) return TRIAL_EXPIRED;
  if (isNotStartedTrial(trial)) return TRIAL_NOT_STARTED;
  if (isPendingTrial(trial)) return TRIAL_PENDING;
  if (isRejectedTrial(trial)) return TRIAL_REJECTED;
  if (subscription && isSubscriptionLocked(subscription.subscriptionStatusId)) {
    return SUBSCRIPTION_INACTIVE;
  }
  return LOCKED;
}

export function getLockoutContent(
  reason: LockoutReason,
  trial: TrialSummary | Nullish,
  dateFormat?: string
): LockoutContent {
  switch (reason) {
    case TRIAL_EXPIRED:
      return {
        title: 'Your free trial has ended',
        message:
          'Your cases and files are safe. Start a subscription to pick up right where you left off.',
        actionLabel: 'Continue with a subscription',
      };
    case TRIAL_NOT_STARTED:
      return {
        title: getNotStartedTitle(trial, dateFormat),
        message:
          "We'll email you as soon as it begins. Nothing else to do until then.",
      };
    case TRIAL_PENDING:
      return {
        title: 'Your trial request is under review',
        message:
          "We're reviewing your request and will email you as soon as it's approved.",
      };
    case TRIAL_REJECTED:
      return {
        title: "Your trial request wasn't approved",
        message:
          'Get in touch and we can talk through the options for your practice.',
        actionLabel: 'Contact support',
      };
    case SUBSCRIPTION_INACTIVE:
      return {
        title: 'Your subscription is inactive',
        message:
          'Your cases and files are safe. Get in touch and we can get your practice running again.',
        actionLabel: 'Contact support',
      };
    case LOCKED:
    default:
      return {
        title: 'This account is locked',
        message:
          'You can still switch to another account or log out. Get in touch and we can help sort this out.',
        actionLabel: 'Contact support',
      };
  }
}

function getNotStartedTitle(
  trial: TrialSummary | Nullish,
  dateFormat?: string
) {
  if (!trial?.startsAt) return 'Your trial has not started yet';
  return `Your trial starts on ${formatTextDate(trial.startsAt, dateFormat)}`;
}
